/**
 * 공정 — **언제 하나**. 공종(`trades.ts`)과는 다른 축이다.
 *
 *   공종 = 누가 하나 (도배팀·타일팀). 견적서 목차이자 협력업체 단위
 *   공정 = 언제 하나 (철거 → 설비 → 목공 → … → 마감). 현장 일정
 *
 * 둘을 한 축으로 묶으면 안 되는 이유는 실제 값이 증명한다 — 견적의 「스타일 공사」
 * 는 시공 순서로 보면 처음(창호)부터 끝(가구)까지 흩어진다. 비용 분류이지 순서가
 * 아니기 때문이다.
 *
 * 9단계로 끊은 근거(2026-09-20 웹 조사) — LX Z:IN 의 3단(기초·수장·마감재)은 너무
 * 굵어 일정에 못 쓰고, 내만집의 22단계는 직원 10명 이하가 관리할 수 없다. 출처들이
 * **공통으로 끊는 지점**만 남겼다.
 *
 * 순서는 **고정 상수가 아니다.** 도배와 마루의 선후는 무몰딩이냐 걸레받이냐에 따라
 * 현장마다 갈린다 — 그래서 견적의 공종이 자기 `phase_id` 를 따로 들고 덮어쓴다.
 */

import { BaseRow } from "../common/types";

/** 내장 기본 공정 코드. 조직이 아무것도 고치지 않았을 때 쓰는 목록이자 바닥이다. */
export const PHASE_CODES = [
  "prep",
  "demolition",
  "plumbing",
  "frame",
  "wet",
  "dry",
  "floor",
  "install",
  "finish",
] as const;
export type PhaseCode = (typeof PHASE_CODES)[number];

/**
 * 공정 한 줄.
 *
 * `org_id` 가 **null 이면 빌드파이 표준**이고 모든 회사가 같이 쓴다. 값이 있으면 그
 * 회사가 추가한 것이다. 표준을 하나 늘려도 배포가 필요 없게 하려고 코드가 아니라
 * 행으로 둔다.
 */
export interface Phase extends Omit<BaseRow, "id"> {
  id: string;
  /** null = 빌드파이 표준. */
  org_id: string | null;
  /** 불변. 이름은 고쳐도 코드는 고치지 않는다. */
  code: string;
  name: string;
  /** 시공 순서. 작을수록 먼저. */
  sort_order: number;
  /** false 면 회사가 숨긴 것. 표준 행은 지우지 못하고 숨기기만 된다. */
  active: boolean;
}

export interface PhaseDef {
  code: PhaseCode;
  name: string;
  sort_order: number;
}

/** 내장 기본 공정 9개. 시드가 이 정의로 표준 행을 만든다. */
export const BUILTIN_PHASES: readonly PhaseDef[] = [
  { code: "prep", name: "준비", sort_order: 1 },
  { code: "demolition", name: "철거", sort_order: 2 },
  { code: "plumbing", name: "설비", sort_order: 3 },
  { code: "frame", name: "골조", sort_order: 4 },
  { code: "wet", name: "습식", sort_order: 5 },
  { code: "dry", name: "건식", sort_order: 6 },
  { code: "floor", name: "바닥", sort_order: 7 },
  { code: "install", name: "설치", sort_order: 8 },
  { code: "finish", name: "마감", sort_order: 9 },
];

/**
 * 표준 행의 id. 코드에서 바로 만든다 — 시드가 돌 때마다 id 가 달라지면
 * 테스트가 흔들리고, 나중에 DB 로 옮길 때 대응표를 따로 들고 있어야 한다.
 */
export const builtinPhaseId = (code: string): string => `phs_${code}`;
